import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { uploadUrl } from '../utils/uploads';
import {
  candidateRecordFromListRow,
  computeProfileCompletionPercent
} from '../utils/profileCompletion';

const SIZES = {
  sm: {
    box: 'w-10 h-10',
    badge: 'text-[8px] min-w-[1.2rem] h-[1.2rem] top-0 right-0',
    icon: 'w-5 h-5'
  },
  md: {
    box: 'w-12 h-12',
    badge: 'text-[9px] min-w-[1.35rem] h-[1.35rem] top-0 right-0',
    icon: 'w-6 h-6'
  },
  lg: {
    box: 'w-14 h-14',
    badge: 'text-[10px] min-w-[1.5rem] h-[1.5rem] top-0 right-0',
    icon: 'w-7 h-7'
  },
  sidebar: {
    box: 'w-[4.5rem] h-[4.5rem]',
    badge: 'text-[9px] min-w-[1.5rem] h-[1.5rem] -top-0.5 -right-0.5',
    icon: 'w-7 h-7'
  }
};

function badgeColor(percent) {
  if (percent >= 100) {
    return 'border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]';
  }
  if (percent >= 60) return 'bg-amber-500 text-white';
  return 'bg-[hsl(var(--ds-muted-fg))] text-white';
}

/**
 * Candidate photo with profile-completion % badge (top-right).
 */
export default function CandidateAvatarWithCompletion({
  candidate,
  user,
  size = 'md',
  linkTo,
  className = '',
  title,
  rounded = 'rounded-full',
  showCompletionBadge = true
}) {
  const record = useMemo(
    () => candidateRecordFromListRow(candidate) || candidate,
    [candidate]
  );
  const percent = useMemo(
    () => computeProfileCompletionPercent(record, user),
    [record, user]
  );

  const photoPath = record?.photo || record?.photoUrl || '';
  const src = photoPath ? uploadUrl(photoPath) : null;
  const label =
    title ||
    record?.displayName ||
    record?.personal?.name ||
    record?.name ||
    'Candidate';

  const s = SIZES[size] || SIZES.md;
  const isSidebar = size === 'sidebar';
  const frameRounded = isSidebar ? 'rounded-xl' : rounded;
  const frameClass = `relative shrink-0 overflow-hidden isolate border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] shadow-sm ${frameRounded} ${s.box} ${className}`;
  const mediaClass = `absolute inset-0 overflow-hidden ${frameRounded}`;

  const inner = (
    <div className={frameClass} title={`${label} — ${percent}% profile complete`}>
      <div className={mediaClass}>
        {src ? (
          <img
            src={src}
            alt={`${label} profile`}
            className="absolute inset-0 h-full w-full object-cover object-center"
            onError={e => {
              e.currentTarget.style.display = 'none';
              const ph = e.currentTarget.nextElementSibling;
              if (ph) ph.classList.remove('hidden');
            }}
          />
        ) : null}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center text-[hsl(var(--ds-muted-fg))] bg-[hsl(var(--ds-muted))] ${
            src ? 'hidden' : ''
          }`}
          aria-hidden={!!src}
        >
          <svg
            className={`${s.icon} text-[hsl(var(--ds-muted-fg))]`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          {size === 'sidebar' && (
            <span className="text-[9px] font-medium text-[hsl(var(--ds-muted-fg))] mt-0.5">Add photo</span>
          )}
        </div>
      </div>
      {showCompletionBadge && (
        <span
          className={`absolute z-[1] flex items-center justify-center rounded-full border-2 border-white font-bold shadow-sm leading-none pointer-events-none ${s.badge} ${badgeColor(percent)}`}
          aria-label={`Profile ${percent}% complete`}
        >
          {percent}%
        </span>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block no-portal-round shrink-0">
        {inner}
      </Link>
    );
  }

  return inner;
}
